import { P } from '@dnb/eufemia';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import classNames from 'classnames';

import { NavNode } from '../../utils';
import { NavigationItem } from './NavigationItem';
import { PlaceholderPage } from './PlaceholderPage';

interface Props {
  pinnedPages: NavNode[];
  recentPages: NavNode[];
}

export function RecentPagesContainer({ pinnedPages, recentPages }: Props) {
  const { setNodeRef, active, isOver, over } = useDroppable({
    id: 'recent-pages-container',
  });

  const pinnedPage = active && pinnedPages.find((page) => page.path === String(active.id));
  const isOverRecentArea = isOver || (over && recentPages.some((page) => page.path === over.id));

  return (
    <div
      className={classNames(
        'flex items-center gap-4 p-2 bg-black-8 rounded-lg',
        pinnedPage && [
          'outline-dashed',
          isOverRecentArea ? 'bg-green-200 outline-green-400' : 'bg-blue-200 outline-blue-400',
        ],
      )}
      ref={setNodeRef}
    >
      {recentPages.length === 0 ? (
        <P className="whitespace-nowrap" size="small">
          Recently visited pages
        </P>
      ) : (
        <>
          {/* Shows a placeholder for where the pinned page will be dropped (always the most recent page) */}
          {pinnedPage && isOverRecentArea && <PlaceholderPage page={pinnedPage} />}
          {recentPages.map((page) => (
            <RecentPageItem key={page.path} node={page} />
          ))}
        </>
      )}
    </div>
  );
}

function RecentPageItem({ node }: { node: NavNode }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: node.path,
  });
  const style = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0 : 1, // Hide original item when dragging as DragOverlay will show a copy
  };

  return (
    <div ref={setNodeRef} {...listeners} {...attributes} style={style}>
      <NavigationItem navNode={node} />
    </div>
  );
}
